import { useTenantNavigate } from '@app-store'
import { useState, useEffect, useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { CustomButton, ExportButtonDropdown, Tabs, Tooltip } from '@ui-kit'
import { isEmpty } from 'lodash'
import {
  getAllEntitiesAgentAction,
  updateEntityName,
  setClientDropdown,
  TableFilterObjectBody,
} from '@app-store'
import { EntityFieldListingProps } from '../../types/entityHomepage.types'
import { useAppDispatch, useAppSelector } from '../../utils/agent.store'
import EntityDataListingTable from './EntityDataListingTable'
import {
  addRoute,
  entitiesRoute,
  entityRoute,
} from '../../routes/route.constant'
import { isReadOnlyUser } from '../../utils/permission'
import {
  CONTEXT_TYPE,
  ENTITY_NAME,
  ENTITY_TABLE_EXPORT_EVENT,
  NAMESPACES,
  PERMISSION_MODULES,
} from '../../utils/constants'
import { permissionObject } from '../../utils/helperFunctions'
import useWatchedRef from '../../hooks/useWatchedRef'
import TableViewsDropdown from '../user-preferences/TableViewsDropdown'
import moment from 'moment'

const EntityFieldListing = ({
  selectedTab,
  isSelected = false,
}: EntityFieldListingProps) => {
  const navigate = useTenantNavigate()
  const dispatch = useAppDispatch()

  const allEntitiesInBookMark = useAppSelector((state) => state.entity.entities)
  const globalPermissions = useAppSelector(
    (state) => state.clientPermissions.globalPermissions
  )
  const entitiesByName = useAppSelector((state) => state.entity.entitiesByName)
  const [globalEntityName, setGlobalEntityName] = useState<string>()
  const searchQuery = useAppSelector((state) => state?.entity?.searchQuery)
  const authUser = useAppSelector((state) => state.authUser)
  // const searchString = useRef<string>('')
  const [redirectEntityId, setRedirectEntityId] = useState<string>('')
  const [isDisableAddButton, setIsDisableAddButton] = useState<boolean>(true)
  const [apiFilter, setApiFilter] = useWatchedRef<TableFilterObjectBody>()
  const [dependencyKey, setDependencyKey] = useState<number>(0)
  const [isMounted, setIsMounted] = useState<boolean>(false)
  const location = useLocation()
  const { entityId } = useParams()

  const handleRedirectId = (id: string, name: string) => {
    setRedirectEntityId(id)
    setIsDisableAddButton(false)
    setGlobalEntityName(name)
  }

  const isGlobalEntity = () => {
    const entity = entitiesByName[globalEntityName ?? '']
    if (!entity || !entity?.source_type) {
      return false
    }
    return entity?.source_type === 'GLOBAL'
  }

  const tabs = useMemo(
    () =>
      allEntitiesInBookMark.map((entity) => ({
        key: entity?.id,
        label: entity?.singular_label ?? '',
        name: entity?.name ?? '',
        component: isMounted ? (
          <EntityDataListingTable
            entityId={entity?.id?.toString()}
            entityName={entity?.name}
            handleRedirectId={handleRedirectId}
            apiFilter={apiFilter}
            setApiFilter={setApiFilter}
          />
        ) : null,
      })),
    [allEntitiesInBookMark, searchQuery, isMounted]
  )

  const [currentTab, setCurrentTab] = useState<{
    label: string
    component: React.ReactNode
    name: string
  } | null>(null)

  const handleTabChange = (label: string, index: number) => {
    if (allEntitiesInBookMark[index]?.id?.toString() !== entityId?.toString()) {
      //as suggested by Arjun Sharma
      setIsDisableAddButton(true)
      setIsMounted(false)
      setDependencyKey((prev) => prev + 1)
    }
    navigate(
      `/${entitiesRoute}/${allEntitiesInBookMark[index]?.id?.toString()}`
    )
  }

  // const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
  //   if (
  //     event.key === NAMESPACES.ENTER ||
  //     (event.key === NAMESPACES.BACKSPACE && searchString?.current === '')
  //   ) {
  //     dispatch(updateSearchQuery(searchString?.current ?? ''))
  //   }
  // }
  // const handleSearch = (query: string) => {
  //   searchString.current = query
  //   if (query === '') {
  //     dispatch(updateSearchQuery(''))
  //   }
  // }
  useEffect(() => {
    dispatch(getAllEntitiesAgentAction())
  }, [authUser.selectedClientTenant?.id])

  useEffect(() => {
    const actions: Record<'true' | 'false', () => void> = {
      true: () => {
        if (!selectedTab) return
        const labelOfSelectedEntity = allEntitiesInBookMark.find(
          (ent) => ent.id.toString() === selectedTab
        )?.singular_label

        const newSelectedTab = tabs.find(
          (tab) => tab.label === labelOfSelectedEntity
        )

        if (newSelectedTab) {
          setCurrentTab(newSelectedTab)
        } else if (allEntitiesInBookMark.length > 0) {
          navigate(
            `/${entitiesRoute}/${allEntitiesInBookMark[0]?.id?.toString()}`
          )
        }
      },
      false: () => {
        if (allEntitiesInBookMark.length > 0) {
          navigate(
            `/${entitiesRoute}/${allEntitiesInBookMark[0]?.id?.toString()}`
          )
        }
      },
    }

    actions[isSelected ? 'true' : 'false']()
  }, [isSelected, selectedTab, allEntitiesInBookMark])

  useEffect(() => {
    if (currentTab?.label) {
      dispatch(updateEntityName({ entityName: currentTab?.label }))
    }
  }, [currentTab])

  useEffect(() => {
    dispatch(setClientDropdown({ isVisible: true, isGlobalVisible: false }))
  }, [location])

  useEffect(() => {
    if (!isMounted) return

    const res = allEntitiesInBookMark.find(
      (entity) => entity.id.toString() === selectedTab
    )

    if (res?.name === 'task' && isEmpty(apiFilter.current?.search_criteria)) {
      setApiFilter({
        search_criteria: {
          start_date: {
            search_key: 'start_date',
            operator: 'BETWEEN',
            search_value: [
              moment().subtract(1, 'months').toISOString(),
              moment().toISOString(),
            ],
          },
        },
      })
      setDependencyKey((prev) => prev + 1)
    }
  }, [selectedTab, allEntitiesInBookMark, isMounted, dependencyKey])

  return (
    <div className="h-full flex flex-col">
      <div className="flex justify-between items-center w-full px-3 py-2 border-b-2">
        <div>
          <div className="font-medium text-base leading-6 font-primary">
            {NAMESPACES.SEARCH}
          </div>
        </div>
        <div className="flex gap-3 justify-end flex-wrap items-center">
          {/* <SvgPipe />
          <div>
            <SearchBar
              width="w-[18rem]"
              height="h-10"
              onSearch={handleSearch}
              showFunnel={false}
              dataQaId="search-entity-list"
              searchLength={1}
              onKeyDown={handleKeyDown}
              showCrossIcon={true}
            />
          </div> */}
          <TableViewsDropdown
            apiFilter={apiFilter}
            setApiFilter={setApiFilter}
            contextType={CONTEXT_TYPE.ENTITY}
            id={parseInt(entityId ?? '')}
            setIsMounted={() => setIsMounted(true)}
            key={entityId}
            setDependencyKey={setDependencyKey}
            dependencyKey={dependencyKey}
            isEntityView
          />
          {!isReadOnlyUser()
            ? !isEmpty(allEntitiesInBookMark) &&
              (isGlobalEntity() && currentTab?.label !== 'Plan'
                ? permissionObject.Manage_Global_Schema(
                    globalPermissions,
                    PERMISSION_MODULES.MANAGE_GLOBAL_DATA
                  )
                : true) && (
                <Tooltip
                  content={
                    currentTab?.name?.toLowerCase() === ENTITY_NAME.CASE_LOWER
                      ? NAMESPACES.CASE_TOOLTIP_MESSAGE
                      : currentTab?.name?.toLowerCase() ===
                        ENTITY_NAME.PRACTICE_LOWER
                      ? NAMESPACES.PRACTICE_TOOLTIP_MESSAGE
                      : currentTab?.name?.toLowerCase() ===
                        ENTITY_NAME.TASK_LOWER
                      ? NAMESPACES.TASK_TOOLTIP_MESSAGE
                      : ''
                  }
                >
                  <CustomButton
                    color="primary"
                    type="button"
                    isFilled={true}
                    disabled={
                      isDisableAddButton ||
                      currentTab?.label?.toLowerCase() ===
                        ENTITY_NAME.CASE_LOWER ||
                      currentTab?.label?.toLowerCase() ===
                        ENTITY_NAME.PRACTICE_LOWER ||
                      currentTab?.label?.toLowerCase() ===
                        ENTITY_NAME.TASK_LOWER ||
                      currentTab?.name?.toLowerCase() ===
                        ENTITY_NAME.COPAY_ID_CARD_NUMBER_VIEW
                    }
                    widthClass="w-width"
                    dataQaId="add-entity-button"
                    onClick={() => {
                      navigate(
                        `/${entityRoute}/${redirectEntityId}/${addRoute}`
                      )
                    }}
                  >
                    <div className="flex items-center">
                      <span className="font-light text-xs">{`Add ${currentTab?.label}`}</span>
                    </div>
                  </CustomButton>
                </Tooltip>
              )
            : null}
        </div>
      </div>

      {!isEmpty(allEntitiesInBookMark) && currentTab ? (
        <div className="flex-1 min-h-0">
          <Tabs
            key={
              searchQuery +
              (authUser.selectedClientTenant?.name_of_organization ?? '') +
              dependencyKey
            }
            tabs={tabs}
            onChange={handleTabChange}
            width="w-full"
            defaultActive={currentTab}
            inActiveFontColor="text-accent-400"
            tabsWrapperClassName="h-full flex flex-col"
            tabsContentClassName=" flex-1 min-h-0"
          >
            {!isReadOnlyUser() && (
              <ExportButtonDropdown
                className="mr-2"
                showCSV={true}
                showExcel={false}
                onExportOption={() =>
                  window.dispatchEvent(new Event(ENTITY_TABLE_EXPORT_EVENT))
                }
                customCSS="-translate-x-3/4"
                dataQaId="correspondence-export-btn"
              />
            )}
          </Tabs>
        </div>
      ) : (
        isEmpty(allEntitiesInBookMark) && (
          <div className="flex justify-center items-center h-full m-auto font-primary text-center  min-h-[25rem] px-2 py-3">
            <div>
              <span>{NAMESPACES.NO_ENTITIES_PRESENT}</span>
            </div>
          </div>
        )
      )}
    </div>
  )
}

export default EntityFieldListing
